import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Get authorization header
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Monetization Service
 * 
 * Frontend service for monetization features:
 * - AVOD (Advertising VOD)
 * - SVOD (Subscription VOD)
 * - VAST/VPAID ads
 * - Pre-roll, Mid-roll, Post-roll
 * - Google Ad Manager
 * - Programmatic Ads
 */

// Get user's monetization settings and available features
export const getMonetizationSettings = async () => {
  try {
    const response = await axios.get(`${API_URL}/monetization/settings`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching monetization settings:', error);
    throw error.response?.data || error;
  }
};

// Update user's monetization settings
export const updateMonetizationSettings = async (settings) => {
  try {
    const response = await axios.put(`${API_URL}/monetization/settings`, settings, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error updating monetization settings:', error);
    throw error.response?.data || error;
  }
};

// Get ad configuration for player
export const getAdConfig = async () => {
  try {
    const response = await axios.get(`${API_URL}/monetization/ad-config`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching ad config:', error);
    throw error.response?.data || error;
  }
};

// Validate VAST/VPAID ad tag
export const validateAdTag = async (vastTagUrl, adType = 'vast') => {
  try {
    const response = await axios.post(
      `${API_URL}/monetization/ads/validate`,
      { vast_tag_url: vastTagUrl, ad_type: adType },
      { headers: getAuthHeader() }
    );
    return response.data;
  } catch (error) {
    console.error('Error validating ad tag:', error);
    throw error.response?.data || error;
  }
};

// Get monetization features info
export const getMonetizationFeatures = async () => {
  try {
    const response = await axios.get(`${API_URL}/monetization/features`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching monetization features:', error);
    throw error.response?.data || error;
  }
};

// Check if user has specific monetization feature
export const checkMonetizationFeature = async (feature) => {
  try {
    const response = await getMonetizationSettings();
    if (!response.success) {
      return false;
    }
    
    const featureMap = {
      'avod': response.features?.allow_avod,
      'svod': response.features?.allow_svod,
      'ads': response.features?.allow_ads,
      'google_ads': response.features?.allow_google_ads,
      'programmatic_ads': response.features?.allow_programmatic_ads,
      'preroll': response.features?.allow_preroll,
      'midroll': response.features?.allow_midroll,
      'postroll': response.features?.allow_postroll
    };
    
    return featureMap[feature] || false;
  } catch (error) {
    console.error('Error checking monetization feature:', error);
    return false;
  }
};

// Default ad configuration
export const defaultAdConfig = {
  enabled: false,
  preroll: {
    enabled: false,
    skipSeconds: 0,
    vastTagUrl: null
  },
  midroll: {
    enabled: false,
    frequencySeconds: 300
  },
  postroll: {
    enabled: false,
    vastTagUrl: null
  },
  googleAds: {
    enabled: false,
    clientId: null,
    slotId: null
  },
  programmatic: {
    enabled: false,
    partner: null
  }
};

export default {
  getMonetizationSettings,
  updateMonetizationSettings,
  getAdConfig,
  validateAdTag,
  getMonetizationFeatures,
  checkMonetizationFeature,
  defaultAdConfig
};

